import React from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

interface PhoneInputFieldProps {
  /** Label text shown above the input */
  label?: string;
  /** Whether the field is required (shows asterisk) */
  required?: boolean;
  /** The full phone value including country dial code, e.g. "971501234567" */
  value: string;
  /** Called on change with the new full phone string */
  onChange: (value: string) => void;
  /** Error message to display below the input */
  error?: string;
  /** Current app theme */
  theme?: "dark" | "light" | string;
  /** Default country ISO2 code, e.g. "ae", "in", "gb" */
  defaultCountry?: string;
  /** Placeholder text for number input portion */
  placeholder?: string;
  /** Whether input is disabled */
  disabled?: boolean;
}

/**
 * Reusable phone input with country flag + dial code selector.
 * Adapts styles for dark / light theme automatically.
 * Uses react-phone-input-2 under the hood.
 */
const PhoneInputField: React.FC<PhoneInputFieldProps> = ({
  label = "Phone Number",
  required = false,
  value,
  onChange,
  error,
  theme = "light",
  defaultCountry = "ae",
  placeholder = "Enter phone number",
  disabled = false,
}) => {
  const isDark = theme === "dark";

  const bgColor = isDark ? "#111111" : "#ffffff";
  const textColor = isDark ? "#f3f3f6" : "#111827";
  const borderColor = error
    ? "#ef4444"
    : isDark
      ? "#2C2C2C"
      : "#d1d5db";
  const placeholderColor = isDark ? "#6b7280" : "#9ca3af";
  const dropdownBg = isDark ? "#1A1A1A" : "#ffffff";
  const dropdownText = isDark ? "#f3f3f6" : "#111827";
  const dropdownHover = isDark ? "#2C2C2C" : "#f3f4f6";
  const buttonBg = isDark ? "#111111" : "#ffffff";
  const buttonBorderColor = error
    ? "#ef4444"
    : isDark
      ? "#2C2C2C"
      : "#d1d5db";
  const labelClass = isDark ? "text-[#F3F3F6]" : "text-gray-700";

  return (
    <div>
      {label && (
        <label className={`block text-sm mb-1 font-normal ${labelClass}`}>
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}

      <style>{`
        /* Override react-phone-input-2 defaults to respect our theme */
        .gymstream-phone .react-tel-input .form-control {
          background-color: ${bgColor} !important;
          color: ${textColor} !important;
          border: 1px solid ${borderColor} !important;
          border-radius: 6px !important;
          width: 100% !important;
          height: 40px !important;
          font-size: 14px !important;
          padding-left: 52px !important;
        }
        .gymstream-phone .react-tel-input .form-control::placeholder {
          color: ${placeholderColor} !important;
        }
        .gymstream-phone .react-tel-input .form-control:focus {
          border-color: #B6CB24 !important;
          outline: none !important;
          box-shadow: 0 0 0 1px #B6CB24 !important;
        }
        .gymstream-phone .react-tel-input .flag-dropdown {
          background-color: ${buttonBg} !important;
          border: 1px solid ${buttonBorderColor} !important;
          border-right: 1px solid ${buttonBorderColor} !important;
          border-radius: 6px 0 0 6px !important;
        }
        .gymstream-phone .react-tel-input .flag-dropdown:hover,
        .gymstream-phone .react-tel-input .flag-dropdown.open {
          background-color: ${isDark ? "#1e1e1e" : "#f9fafb"} !important;
        }
        .gymstream-phone .react-tel-input .selected-flag {
          background-color: transparent !important;
          padding: 0 8px !important;
        }
        .gymstream-phone .react-tel-input .selected-flag .arrow {
          border-top-color: ${isDark ? "#9ca3af" : "#6b7280"} !important;
        }
        .gymstream-phone .react-tel-input .country-list {
          background-color: ${dropdownBg} !important;
          border: 1px solid ${isDark ? "#2C2C2C" : "#e5e7eb"} !important;
          box-shadow: 0 8px 24px rgba(0,0,0,0.4) !important;
          border-radius: 6px !important;
          color: ${dropdownText} !important;
          max-height: 220px !important;
          z-index: 9999 !important;
        }
        .gymstream-phone .react-tel-input .country-list .country {
          padding: 8px 12px !important;
          color: ${dropdownText} !important;
        }
        .gymstream-phone .react-tel-input .country-list .country:hover {
          background-color: ${dropdownHover} !important;
        }
        .gymstream-phone .react-tel-input .country-list .country.highlight {
          background-color: ${isDark ? "#333333" : "#f0f0f0"} !important;
        }
        .gymstream-phone .react-tel-input .country-list .search {
          background-color: ${dropdownBg} !important;
          padding: 8px 12px !important;
        }
        .gymstream-phone .react-tel-input .country-list .search-box {
          background-color: ${isDark ? "#111111" : "#ffffff"} !important;
          border: 1px solid ${isDark ? "#2C2C2C" : "#d1d5db"} !important;
          color: ${dropdownText} !important;
          border-radius: 4px !important;
          padding: 4px 8px !important;
          width: 100% !important;
          font-size: 13px !important;
        }
        .gymstream-phone .react-tel-input .country-list .country .dial-code {
          color: ${isDark ? "#9ca3af" : "#6b7280"} !important;
        }
      `}</style>

      <div className="gymstream-phone">
        <PhoneInput
          country={defaultCountry as any}
          value={value}
          onChange={onChange}
          disabled={disabled}
          placeholder={placeholder}
          enableSearch
          searchPlaceholder="Search country..."
          disableSearchIcon={false}
          inputProps={{
            required,
          }}
          // 👇 Add this line to disable hyphens, spaces, and brackets in the UI
          autoFormat={false}
        />
      </div>

      {error && (
        <p className="text-red-500 text-xs mt-1">{error}</p>
      )}
    </div>
  );
};

export default PhoneInputField;
